// Carte d'une affaire dans la liste. La priorité se lit par la pastille, pas par une bordure colorée.

import {
  ACTION_LABELS,
  DATE_LABELS,
  eur,
  eurSigned,
  type Affaire,
  type PlanRow,
  type PlanView,
  type TraitementDto,
  isAffaire,
} from '@cockpit/shared';
import { Badge, Card, cn } from '@cockpit/ui';
import { ChevronRight } from 'lucide-react';
import { DivergingBars } from '../charts.js';
import { DoneButton, PrioBadge, Stake, entLabel, shortDate } from './bits.js';

function Meta({ r }: { r: Affaire }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink-3">
      <span>
        CA <b className="tnum font-semibold text-ink">{eur(r.ca)}</b>
      </span>
      <span>
        Facturé <b className="tnum font-semibold text-ink">{r.fa} %</b> · coûts{' '}
        <b className="tnum font-semibold text-ink">{r.cr} %</b>
      </span>
      <span>
        Marge{' '}
        <b className={cn('tnum font-semibold', r.mr < r.mp - 0.05 ? 'text-danger-700' : 'text-ink')}>
          {r.mp.toLocaleString('fr-FR')} % → {r.mr.toLocaleString('fr-FR')} %
        </b>
      </span>
      <span className="tnum">
        {r.deb ? `Du ${shortDate(r.deb)}` : ''}
        {r.fin ? ` au ${shortDate(r.fin)}` : ' · fin non renseignée'}
      </span>
      <span>
        {r.com ?? '—'} · cond. {r.cond ?? <span className="italic">non affecté</span>}
      </span>
    </div>
  );
}

export function AffaireCard({
  row,
  view,
  showEnt,
  done,
  info,
  onToggle,
  onOpen,
}: {
  row: PlanRow;
  view: PlanView;
  showEnt: boolean;
  done: boolean;
  info?: TraitementDto;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const a = isAffaire(row) ? row : null;
  const badge =
    view === 'plan' && a ? (
      <PrioBadge prio={a.prio} />
    ) : view === 'drift' && a ? (
      <Badge tone={a.drift?.ratt ? 'success' : 'neutral'} dot>
        {a.drift?.ratt ? 'Rattrapable' : 'Constatée'}
      </Badge>
    ) : view === 'dates' && a ? (
      <Badge tone="warn" dot>
        {a.dates.length} alerte{a.dates.length > 1 ? 's' : ''}
      </Badge>
    ) : (
      <Badge tone={(row.sav?.net ?? 0) > 0 ? 'danger' : 'success'} dot>
        {(row.sav?.net ?? 0) > 0 ? 'Perte nette' : 'Gain net'}
      </Badge>
    );

  const stake =
    view === 'plan' && a ? (
      <Stake value={eur(a.enj)} label="enjeu" />
    ) : view === 'drift' && a ? (
      <Stake value={eur(a.der)} label="marge perdue" tone="bad" />
    ) : view === 'dates' && a ? (
      <Stake value={`${a.cr} %`} label="coûts engagés" />
    ) : (
      <Stake
        value={eurSigned(row.sav?.net ?? 0)}
        label="net achats"
        tone={(row.sav?.net ?? 0) > 0 ? 'bad' : 'good'}
      />
    );

  return (
    <Card
      padded={false}
      interactive
      className={cn('transition-opacity duration-base', done && 'opacity-55 hover:opacity-100')}
    >
      <div className="grid gap-x-6 gap-y-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            {badge}
            {done && <Badge tone="success">Traité</Badge>}
            <button
              type="button"
              onClick={onOpen}
              className="min-w-0 text-left font-display text-[16px] font-bold leading-snug text-ink hover:text-navy-600 [overflow-wrap:anywhere]"
            >
              {row.nom ?? row.cli ?? row.code}
            </button>
            {row.type && <Badge tone="azur">{row.type}</Badge>}
          </div>
          <p className="mt-1 text-[13px] text-ink-3">
            <span className="tnum">{row.code}</span>
            {showEnt && ` · ${entLabel(row.ent)}`} · Client : {row.cli ?? '—'}
          </p>
          <div className="mt-2">
            {a ? <Meta r={a} /> : <p className="text-[13px] text-ink-3">Hors suivi d’affaire actif</p>}
          </div>
        </div>
        <div className="flex items-start justify-between gap-4 md:flex-col md:items-end">
          {stake}
          {view === 'plan' && a?.due && (
            <span className="tnum rounded-sm bg-navy-50 px-2 py-0.5 text-[12px] font-semibold text-navy-600">
              échéance {shortDate(a.due)}
            </span>
          )}
        </div>
      </div>

      {view === 'plan' && a && (
        <ul className="border-t border-line-soft px-5 py-2">
          {a.acts.map((x) => (
            <li
              key={x.k}
              className="grid gap-1 py-1.5 text-[14px] sm:grid-cols-[150px_minmax(0,1fr)_auto] sm:gap-4"
            >
              <span
                className={cn(
                  'text-[12px] font-semibold uppercase leading-6 tracking-[0.06em]',
                  x.w || x.k === 'loss' ? 'text-danger-700' : 'text-ink-3',
                )}
              >
                {ACTION_LABELS[x.k]}
              </span>
              <span className="text-ink">{x.txt}</span>
              <span className="tnum text-[13px] text-ink-3">{eur(x.e)}</span>
            </li>
          ))}
          {a.dates.length > 0 && (
            <li className="grid gap-1 py-1.5 text-[14px] sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4">
              <span className="text-[12px] font-semibold uppercase leading-6 tracking-[0.06em] text-warn-700">
                Date douteuse
              </span>
              <span className="text-ink-2">{a.dates.map((d) => d.txt).join(' · ')}</span>
            </li>
          )}
        </ul>
      )}

      {view === 'dates' && a && (
        <ul className="border-t border-line-soft px-5 py-2">
          {a.dates.map((x) => (
            <li
              key={x.k}
              className="grid gap-1 py-1.5 text-[14px] sm:grid-cols-[190px_minmax(0,1fr)] sm:gap-4"
            >
              <span className="text-[12px] font-semibold uppercase leading-6 tracking-[0.06em] text-warn-700">
                {DATE_LABELS[x.k]}
              </span>
              <span className="text-ink">{x.txt}</span>
            </li>
          ))}
          {a.est && (
            <li className="grid gap-1 py-1.5 text-[14px] sm:grid-cols-[190px_minmax(0,1fr)] sm:gap-4">
              <span className="text-[12px] font-semibold uppercase leading-6 tracking-[0.06em] text-navy-600">
                Fin estimée
              </span>
              <span className="text-ink">
                Vers le <b className="tnum">{shortDate(a.est)}</b> au rythme actuel des coûts (début{' '}
                {shortDate(a.deb)}, {a.cr} % engagés).
              </span>
            </li>
          )}
        </ul>
      )}

      {view === 'drift' && a?.drift && (
        <div className="border-t border-line-soft px-5 py-3">
          <DivergingBars
            fmt={eurSigned}
            rows={[
              { label: 'Achats', value: a.drift.dA },
              { label: 'MO + ST', value: a.drift.dM },
              { label: 'Autres charges', value: a.drift.dO },
            ]}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-soft bg-surface-2/50 px-5 py-2">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-navy-600 hover:underline"
        >
          {a?.dec
            ? `Pourquoi la marge bouge · ${eurSigned(a.der)}`
            : row.sav
              ? `Détail des achats · ${row.sav.n} ligne${row.sav.n > 1 ? 's' : ''}`
              : 'Voir le détail'}
          <ChevronRight size={16} strokeWidth={1.75} />
        </button>
        <DoneButton done={done} info={info} onToggle={onToggle} />
      </div>
    </Card>
  );
}
